import { useCallback, useEffect, useMemo, useState } from "react";
import { CgEye } from "react-icons/cg";
import { MdOutlineFolderDelete } from "react-icons/md";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { formatDateTime } from "../../utils/date";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";
import PrescriptionDetailModal from "./PrescriptionDetailModal";
import {
  ADMIN_STATUSES,
  FilesCell,
  STATUS_FILTERS,
  StatusBadge,
  canDeleteFiles,
  deleteFilesBlockedReason,
  isCancelled,
  suggestionSummary,
} from "./prescriptionUtils";

const PrescriptionsList = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewPrescription, setViewPrescription] = useState(null);
  // Rows whose files are about to be deleted (one for a row action, many for bulk).
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const notify = useCallback((message, type = "success") => {
    setSnack({ notification: { message, type } });
  }, []);

  const getPrescriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageNo, limit });
      if (statusFilter) params.set("status", statusFilter);
      const response = await AuthInterceptor(`/admin/all-prescriptions?${params}`, { method: "GET" });
      const data = response.data;
      if (data?.s === 200) {
        setPrescriptions(data.r || []);
        setTotalRecords(data.tr || 0);
      } else {
        setPrescriptions([]);
        setTotalRecords(0);
        if (data?.m && data?.s !== 404) notify(data.m, "error");
      }
    } catch (error) {
      setPrescriptions([]);
      setTotalRecords(0);
      notify("Error fetching prescriptions", "error");
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, statusFilter, notify]);

  useEffect(() => {
    getPrescriptions();
  }, [getPrescriptions]);

  // Search is applied to the current page (the API has no text search).
  const filteredPrescriptions = useMemo(() => {
    if (!query.trim()) return prescriptions;
    const search = query.toLowerCase();
    return prescriptions.filter((item) => {
      const user = item.userDetails || {};
      return (
        String(item._id || "").toLowerCase().includes(search) ||
        String(user.fullName || "").toLowerCase().includes(search) ||
        String(user.mobileNumber || "").toLowerCase().includes(search) ||
        String(item.member?.fullName || "").toLowerCase().includes(search)
      );
    });
  }, [query, prescriptions]);

  /** Replace one prescription in the list (and the open detail view) with the server copy. */
  const applyUpdate = useCallback((updated) => {
    if (!updated?._id) return;
    setPrescriptions((prev) => prev.map((item) => (item._id === updated._id ? { ...item, ...updated } : item)));
    setViewPrescription((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated } : prev));
  }, []);

  const visibleIds = filteredPrescriptions.map((item) => item._id).filter(Boolean);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const selectedRows = prescriptions.filter((item) => selectedIds.includes(item._id));
  const eligibleSelected = selectedRows.filter(canDeleteFiles);

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const handleStatusChange = async (prescription, status) => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/prescriptions/${prescription._id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });
      if (response?.data?.s === 200) {
        applyUpdate(response.data.r);
        notify(response.data.m || "Prescription status updated");
        // The row may no longer match the active status filter.
        if (statusFilter) getPrescriptions();
      } else {
        notify(response?.data?.m || "Error updating prescription", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmBulkDeleteFiles = () => {
    if (!eligibleSelected.length) {
      notify("Select at least one Solved or Cancelled prescription with files", "error");
      return;
    }
    setDeleteTargets(eligibleSelected);
  };

  const deleteFiles = async () => {
    const ids = deleteTargets.map((item) => item._id);
    if (!ids.length) return;
    setLoading(true);
    try {
      const single = ids.length === 1;
      const response = await AuthInterceptor(
        single ? `/admin/prescriptions/${ids[0]}/files` : "/admin/prescriptions/files",
        { method: "DELETE", body: single ? undefined : JSON.stringify({ ids }) }
      );
      if (response?.data?.s === 200) {
        notify(response.data.m || "Prescription files deleted");
        if (single) applyUpdate(response.data.r);
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        getPrescriptions();
        // Refresh the open detail view too (bulk returns only counts).
        if (viewPrescription && ids.includes(viewPrescription._id) && !single) {
          const detail = await AuthInterceptor(`/admin/prescriptions/${viewPrescription._id}`, { method: "GET" });
          if (detail?.data?.s === 200) applyUpdate(detail.data.r);
        }
      } else {
        notify(response?.data?.m || "Error deleting prescription files", "error");
      }
    } finally {
      setLoading(false);
      setDeleteTargets([]);
    }
  };

  const fileCount = deleteTargets.reduce((sum, item) => sum + (item.files?.length || 0), 0);
  const skippedCount = selectedRows.length - eligibleSelected.length;
  const bulkTitle = eligibleSelected.length
    ? `Delete files of ${eligibleSelected.length} prescription(s)`
    : "Select Solved or Cancelled prescriptions that still have files";

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Prescriptions"
          searchValue={query}
          searchPlaceholder="Search user, mobile or member..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        >
          <select
            className="form-select form-select-sm"
            style={{ width: "160px" }}
            value={statusFilter}
            onChange={(event) => {
              setPageNo(1);
              setSelectedIds([]);
              setStatusFilter(event.target.value);
            }}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </ListHeader>

        {/* Bulk actions (only when there are rows to select) */}
        {filteredPrescriptions.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-muted small">{selectedIds.length} selected</span>
            <span title={bulkTitle}>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={confirmBulkDeleteFiles}
                disabled={!eligibleSelected.length}
              >
                Delete files{eligibleSelected.length ? ` (${eligibleSelected.length})` : ""}
              </button>
            </span>
            {selectedIds.length > 0 && skippedCount > 0 && (
              <span className="text-muted small">
                {skippedCount} selected not eligible (only Solved/Cancelled with files)
              </span>
            )}
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%", minWidth: "1050px" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "44px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all prescriptions on this page"
                  />
                </th>
                <th style={{ width: "56px" }}>Sr.</th>
                <th>User</th>
                <th>Member</th>
                <th style={{ width: "170px" }}>Files</th>
                <th style={{ width: "140px" }}>Upload Date</th>
                <th style={{ width: "170px" }}>Suggestion</th>
                <th style={{ width: "140px" }}>Status</th>
                <th style={{ width: "110px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                filteredPrescriptions.map((item, index) => {
                  const deletable = canDeleteFiles(item);
                  return (
                    <tr key={item._id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.includes(item._id)}
                          onChange={() => toggleRowSelection(item._id)}
                          aria-label={`Select prescription ${item._id}`}
                        />
                      </td>
                      <td>{(pageNo - 1) * limit + index + 1}</td>
                      <td>
                        <div className="fw-semibold text-truncate" title={item.userDetails?.fullName}>
                          {item.userDetails?.fullName || "-"}
                        </div>
                        <div className="text-muted small">
                          {item.userDetails?.mobileNumber || item.mobileNumber || "-"}
                        </div>
                      </td>
                      <td>
                        <div className="text-truncate" title={item.member?.fullName}>
                          {item.member?.fullName || "-"}
                        </div>
                        {item.member?.relation && <div className="text-muted small">{item.member.relation}</div>}
                      </td>
                      <td>
                        <FilesCell item={item} />
                      </td>
                      <td className="small">{formatDateTime(item.createdAt)}</td>
                      <td className="small">
                        {item.suggestion ? suggestionSummary(item.suggestion) : <span className="text-muted">-</span>}
                      </td>
                      <td>
                        {isCancelled(item) ? (
                          <span title="Cancelled by the user; status can't be changed">
                            <StatusBadge status="Cancelled" />
                          </span>
                        ) : (
                          <select
                            className="form-select form-select-sm"
                            value={item.status || "Open"}
                            onChange={(event) => handleStatusChange(item, event.target.value)}
                            aria-label={`Change status for prescription ${item._id}`}
                          >
                            {ADMIN_STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => setViewPrescription(item)}
                            title="View & work on prescription"
                          >
                            <CgEye size={16} />
                          </button>
                          <span title={deletable ? "Delete files" : deleteFilesBlockedReason(item)}>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              disabled={!deletable}
                              onClick={() => setDeleteTargets([item])}
                              aria-label="Delete files"
                            >
                              <MdOutlineFolderDelete size={16} />
                            </button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {!loading && filteredPrescriptions.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No prescriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination show={totalRecords > 0} pageNo={pageNo} totalPages={totalPages} onPageChange={setPageNo} />
      </div>

      {viewPrescription && (
        <PrescriptionDetailModal
          prescription={viewPrescription}
          onHide={() => setViewPrescription(null)}
          onUpdated={applyUpdate}
          onDeleteFiles={(item) => setDeleteTargets([item])}
          notify={notify}
        />
      )}

      <DeleteConfirmModal
        show={deleteTargets.length > 0}
        count={deleteTargets.length}
        singular="prescription"
        plural="prescriptions"
        loading={loading}
        title="Delete Files"
        confirmLabel="Delete files"
        message={
          <p>
            {deleteTargets.length === 1 ? (
              <>
                Delete <strong>{fileCount}</strong> file{fileCount === 1 ? "" : "s"} of the prescription from{" "}
                <strong>{deleteTargets[0]?.userDetails?.fullName || "this user"}</strong>
                {deleteTargets[0]?.member?.fullName ? ` (member: ${deleteTargets[0].member.fullName})` : ""}?
              </>
            ) : (
              <>
                Delete the files of <strong>{deleteTargets.length}</strong> prescriptions ({fileCount} files)?
              </>
            )}
          </p>
        }
        onConfirm={deleteFiles}
        onCancel={() => setDeleteTargets([])}
      >
        <div className="alert alert-info py-2 small">
          Only the uploaded files are removed permanently. The prescription record, member, suggestion and
          activity history stay, and the user will see "Files removed".
        </div>
      </DeleteConfirmModal>
    </>
  );
};

export default PrescriptionsList;
